import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColGroupDef, themeQuartz } from 'ag-grid-community';
import { IEventUKCConformationCount } from '@eagerdog/interfaces';
import Button from '../Button/Button';

import './Counts.scss';

interface IProps {
  onClose: () => void;
  buttonsDisabled?: boolean;
  tableCounts: IEventUKCConformationCount[];
}

const Counts: React.FC<IProps> = (props) => {

  // Filter data for regular conformation (has at least one non-altered entry)
  const regularData = useMemo(() => {
    return props.tableCounts.filter(row => 
      (row.male_count || 0) > 0 || 
      (row.female_count || 0) > 0 || 
      (row.champion_count || 0) > 0 || 
      (row.grand_champion_count || 0) > 0 ||
      (row.veteran_count || 0) > 0 ||
      (row.novice_puppy_count || 0) > 0 ||
      (row.brace_count || 0) > 0 ||
      (row.junior_showmanship_count || 0) > 0 ||
      (row.total_dog_count || 0) > 0 ||
      (row.total_junior_count || 0) > 0
    );
  }, [props.tableCounts]);

  // Filter data for altered conformation (has at least one altered entry)
  const alteredData = useMemo(() => {
    return props.tableCounts.filter(row => 
      (row.alt_male_count || 0) > 0 || 
      (row.alt_female_count || 0) > 0 || 
      (row.alt_champion_count || 0) > 0 || 
      (row.alt_grand_champion_count || 0) > 0 ||
      (row.alt_veteran_count || 0) > 0 ||
      (row.alt_novice_puppy_count || 0) > 0 ||
      (row.alt_brace_count || 0) > 0 ||
      (row.junior_showmanship_count || 0) > 0 ||
      (row.total_dog_count || 0) > 0 ||
      (row.total_junior_count || 0) > 0
    );
  }, [props.tableCounts]);

  // Calculate totals for regular conformation
  const regularTotals = useMemo(() => {
    return regularData.reduce((acc, row) => ({
      breed: 'Total',
      breed_group: '',
      male_count: acc.male_count + (row.male_count || 0),
      female_count: acc.female_count + (row.female_count || 0),
      champion_count: acc.champion_count + (row.champion_count || 0),
      grand_champion_count: acc.grand_champion_count + (row.grand_champion_count || 0),
      veteran_count: acc.veteran_count + (row.veteran_count || 0),
      novice_puppy_count: acc.novice_puppy_count + (row.novice_puppy_count || 0),
      brace_count: acc.brace_count + (row.brace_count || 0),
      junior_showmanship_count: acc.junior_showmanship_count + (row.junior_showmanship_count || 0),
      total_dog_count: acc.total_dog_count + (row.total_dog_count || 0),
      total_junior_count: acc.total_junior_count + (row.total_junior_count || 0)
    }), {
      breed: 'Total',
      breed_group: '',
      male_count: 0,
      female_count: 0,
      champion_count: 0,
      grand_champion_count: 0,
      veteran_count: 0,
      novice_puppy_count: 0,
      brace_count: 0,
      junior_showmanship_count: 0,
      total_dog_count: 0,
      total_junior_count: 0
    });
  }, [regularData]);

  // Calculate totals for altered conformation
  const alteredTotals = useMemo(() => {
    return alteredData.reduce((acc, row) => ({
      breed: 'Total',
      breed_group: '',
      alt_male_count: acc.alt_male_count + (row.alt_male_count || 0),
      alt_female_count: acc.alt_female_count + (row.alt_female_count || 0),
      alt_champion_count: acc.alt_champion_count + (row.alt_champion_count || 0),
      alt_grand_champion_count: acc.alt_grand_champion_count + (row.alt_grand_champion_count || 0),
      alt_veteran_count: acc.alt_veteran_count + (row.alt_veteran_count || 0),
      alt_novice_puppy_count: acc.alt_novice_puppy_count + (row.alt_novice_puppy_count || 0),
      alt_brace_count: acc.alt_brace_count + (row.alt_brace_count || 0),
      junior_showmanship_count: acc.junior_showmanship_count + (row.junior_showmanship_count || 0),
      total_dog_count: acc.total_dog_count + (row.total_dog_count || 0),
      total_junior_count: acc.total_junior_count + (row.total_junior_count || 0)
    }), {
      breed: 'Total',
      breed_group: '',
      alt_male_count: 0,
      alt_female_count: 0,
      alt_champion_count: 0,
      alt_grand_champion_count: 0,
      alt_veteran_count: 0,
      alt_novice_puppy_count: 0,
      alt_brace_count: 0,
      junior_showmanship_count: 0,
      total_dog_count: 0,
      total_junior_count: 0
    });
  }, [alteredData]);

  // Column definitions for regular conformation
  const regularColumnDefs: (ColDef | ColGroupDef)[] = useMemo(() => [
    {
      field: 'breed_group',
      headerName: 'Breed Group',
      rowGroup: true,
      hide: true,
      enableRowGroup: true
    },
    {
      field: 'breed',
      headerName: 'Breed',
      minWidth: 250,
      cellRenderer: 'agGroupCellRenderer'
    },
    {
      field: 'male_count',
      headerName: 'M',
      aggFunc: 'sum',
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'female_count',
      headerName: 'F',
      aggFunc: 'sum',
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'champion_count',
      headerName: 'CH',
      aggFunc: 'sum',
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'grand_champion_count',
      headerName: 'GRCH',
      aggFunc: 'sum',
      width: 95,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'veteran_count',
      headerName: 'VET',
      aggFunc: 'sum',
      width: 90,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'novice_puppy_count',
      headerName: 'NP',
      aggFunc: 'sum',
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'brace_count',
      headerName: 'BR',
      aggFunc: 'sum',
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'junior_showmanship_count',
      headerName: 'Junior',
      aggFunc: 'sum',
      width: 100,
      cellClass: 'special-class-column',
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'total_dog_count',
      headerName: 'Total Dog',
      aggFunc: 'sum',
      width: 120,
      cellClass: 'special-class-column',
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'total_junior_count',
      headerName: 'Total Junior',
      aggFunc: 'sum',
      width: 145,
      cellClass: 'special-class-column',
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    }
  ], []);

  // Column definitions for altered conformation
  const alteredColumnDefs: (ColDef | ColGroupDef)[] = useMemo(() => [
    {
      field: 'breed_group',
      headerName: 'Breed Group',
      rowGroup: true,
      hide: true,
      enableRowGroup: true
    },
    {
      field: 'breed',
      headerName: 'Breed',
      minWidth: 250,
      cellRenderer: 'agGroupCellRenderer'
    },
    {
      field: 'alt_male_count',
      headerName: 'M',
      aggFunc: 'sum',
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'alt_female_count',
      headerName: 'F',
      aggFunc: 'sum',
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'alt_champion_count',
      headerName: 'CH',
      aggFunc: 'sum',
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'alt_grand_champion_count',
      headerName: 'GRCH',
      aggFunc: 'sum',
      width: 95,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'alt_veteran_count',
      headerName: 'VET',
      aggFunc: 'sum',
      width: 90,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'alt_novice_puppy_count',
      headerName: 'NP',
      aggFunc: 'sum',
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'alt_brace_count',
      headerName: 'BR',
      aggFunc: 'sum',
      width: 80,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'junior_showmanship_count',
      headerName: 'Junior',
      aggFunc: 'sum',
      width: 100,
      cellClass: 'special-class-column',
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'total_dog_count',
      headerName: 'Total Dog',
      aggFunc: 'sum',
      width: 120,
      cellClass: 'special-class-column',
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'total_junior_count',
      headerName: 'Total Junior',
      aggFunc: 'sum',
      width: 145,
      cellClass: 'special-class-column',
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    }
  ], []);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: false,
    resizable: false,
    filter: false
  }), []);

  const autoGroupColumnDef = useMemo<ColDef>(() => ({
    headerName: 'Breed Group / Breed',
    width: 250,
    minWidth: 250,
    maxWidth: 250,
    cellRendererParams: {
      suppressCount: true
    }
  }), []);

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
  });

  return (
    <div className="Counts">
      <div className="title">Counts</div>
      <p>Below are the counts for this show/trial.</p>
      
      {/* Regular Conformation Section */}
      <div className="counts-section">
        <h3 className="section-title">Regular Conformation</h3>
        {regularData.length > 0 ? (
          <div className="counts-grid">
            <AgGridReact
              theme={theme}
              rowData={regularData}
              columnDefs={regularColumnDefs}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              groupDefaultExpanded={-1}
              suppressAggFuncInHeader={true}
              animateRows={false}
              domLayout="autoHeight"
              popupParent={document.body}
              pinnedBottomRowData={[regularTotals]}
            />
          </div>
        ) : (
          <div className="emptyTable">
            <span>No regular conformation entries found.</span>
          </div>
        )}
      </div>
      
      {/* Altered Conformation Section */}
      <div className="counts-section">
        <h3 className="section-title">Altered Conformation</h3>
        {alteredData.length > 0 ? (
          <div className="counts-grid">
            <AgGridReact
              theme={theme}
              rowData={alteredData}
              columnDefs={alteredColumnDefs}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              groupDefaultExpanded={-1}
              suppressAggFuncInHeader={true}
              animateRows={false}
              domLayout="autoHeight"
              popupParent={document.body}
              pinnedBottomRowData={[alteredTotals]}
            />
          </div>
        ) : (
          <div className="emptyTable">
            <span>No altered conformation entries found.</span>
          </div>
        )}
      </div>
      
      <div className="button-group">
        <Button disabled={props.buttonsDisabled} onClick={props.onClose}>Close</Button>
      </div>
    </div>
  );
}

export default Counts;
